import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Save, Eye, EyeOff, ChevronUp, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';

/**
 * AdminTestimonialsTab — main-admin only.
 *
 *  • CRUD on testimonials (quote, name, city, is_active, position)
 *  • Overrides for the landing-page stats bar (display member count + city count + rating label)
 */
export default function AdminTestimonialsTab({ getAuthHeader, isMainAdmin = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ quote: '', name: '', city: '', is_active: true });

  const [statsCfg, setStatsCfg] = useState(null);
  const [savingStats, setSavingStats] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get('/admin/testimonials', { headers: getAuthHeader() }),
        api.get('/admin/landing/stats', { headers: getAuthHeader() }),
      ]);
      setItems(t.data.testimonials || []);
      setStatsCfg(s.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isMainAdmin) {
    return <p className="text-text-mid p-6">Main admin only.</p>;
  }

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm({ quote: '', name: 'Mom Name', city: 'California', is_active: true });
  };

  const openEdit = (t) => {
    setCreating(false);
    setEditing(t.id);
    setForm({ quote: t.quote, name: t.name, city: t.city, is_active: t.is_active });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const submit = async () => {
    if (!form.quote.trim()) { toast.error('Quote is required'); return; }
    setSaving(true);
    try {
      if (creating) {
        await api.post('/admin/testimonials', form, { headers: getAuthHeader() });
        toast.success('Testimonial created');
      } else if (editing) {
        await api.patch(`/admin/testimonials/${editing}`, form, { headers: getAuthHeader() });
        toast.success('Testimonial updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { is_active: !t.is_active }, { headers: getAuthHeader() });
      toast.success(t.is_active ? 'Hidden from landing page' : 'Now visible on landing page');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const move = async (t, direction) => {
    const idx = items.findIndex(i => i.id === t.id);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const other = items[target];
    try {
      await Promise.all([
        api.patch(`/admin/testimonials/${t.id}`, { position: other.position }, { headers: getAuthHeader() }),
        api.patch(`/admin/testimonials/${other.id}`, { position: t.position }, { headers: getAuthHeader() }),
      ]);
      await load();
    } catch (err) {
      toast.error('Reorder failed');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial by ${t.name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const saveStats = async () => {
    setSavingStats(true);
    try {
      const payload = {
        override_member_count: !!statsCfg.override_member_count,
        member_count: statsCfg.member_count ?? null,
        override_city_count: !!statsCfg.override_city_count,
        city_count: statsCfg.city_count ?? null,
        rating_label: statsCfg.rating_label || '5-Star Community Rating',
      };
      const res = await api.patch('/admin/landing/stats', payload, { headers: getAuthHeader() });
      setStatsCfg(res.data);
      toast.success('Stats bar updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingStats(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-testimonials-tab">
      {/* ─── Stats Bar config ─── */}
      <section className="bg-white rounded-2xl shadow-card p-6 border border-border-subtle" data-testid="landing-stats-config">
        <h3 className="font-display text-lg text-navy mb-1">Landing Page — Stats Bar</h3>
        <p className="text-xs text-text-mid mb-4">By default the member count and city count are live from the database. Toggle overrides if you want fixed display values.</p>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="flex items-center gap-2 text-sm mb-2 text-text-mid">
              <input
                type="checkbox"
                checked={!!statsCfg?.override_member_count}
                onChange={(e) => setStatsCfg(c => ({ ...c, override_member_count: e.target.checked }))}
                data-testid="override-members-checkbox"
              />
              Override member count
            </label>
            <Input
              type="number"
              value={statsCfg?.member_count ?? ''}
              onChange={(e) => setStatsCfg(c => ({ ...c, member_count: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
              placeholder="e.g. 200"
              disabled={!statsCfg?.override_member_count}
              className="rounded-xl"
              data-testid="member-count-input"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm mb-2 text-text-mid">
              <input
                type="checkbox"
                checked={!!statsCfg?.override_city_count}
                onChange={(e) => setStatsCfg(c => ({ ...c, override_city_count: e.target.checked }))}
                data-testid="override-cities-checkbox"
              />
              Override city count
            </label>
            <Input
              type="number"
              value={statsCfg?.city_count ?? ''}
              onChange={(e) => setStatsCfg(c => ({ ...c, city_count: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
              placeholder="e.g. 5"
              disabled={!statsCfg?.override_city_count}
              className="rounded-xl"
              data-testid="city-count-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-sm text-text-mid">Rating label (third stat)</Label>
            <Input
              value={statsCfg?.rating_label || ''}
              onChange={(e) => setStatsCfg(c => ({ ...c, rating_label: e.target.value }))}
              placeholder="5-Star Community Rating"
              className="rounded-xl"
              data-testid="rating-label-input"
            />
          </div>
        </div>

        <div className="mt-5">
          <Button
            onClick={saveStats}
            disabled={savingStats}
            className="bg-teal hover:bg-teal-deep text-white rounded-full"
            data-testid="save-stats-btn"
          >
            {savingStats ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            Save Stats Bar
          </Button>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="bg-white rounded-2xl shadow-card p-6 border border-border-subtle">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg text-navy">Testimonials</h3>
            <p className="text-xs text-text-mid">Showcased on the landing page (first 3 active by position).</p>
          </div>
          <Button onClick={openCreate} className="bg-teal hover:bg-teal-deep text-white rounded-full" data-testid="add-testimonial-btn">
            <Plus size={14} className="mr-1" /> New Testimonial
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-text-mid">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-text-light">No testimonials yet. Add three to fill the landing-page row.</p>
        ) : (
          <div className="space-y-3" data-testid="testimonials-admin-list">
            {items.map((t, i) => (
              <div
                key={t.id}
                className={`border rounded-xl p-4 ${t.is_active ? 'border-border-subtle bg-cream/30' : 'border-dashed border-text-light/40 bg-gray-50 opacity-70'}`}
                data-testid={`admin-testimonial-row-${t.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-navy line-clamp-3">&ldquo;{t.quote}&rdquo;</p>
                    <p className="text-xs text-text-mid mt-2">— {t.name}, {t.city}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => move(t, 'up')} disabled={i === 0} className="h-7 w-7">
                        <ChevronUp size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => move(t, 'down')} disabled={i === items.length - 1} className="h-7 w-7">
                        <ChevronDown size={14} />
                      </Button>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-7 text-xs" data-testid={`edit-testimonial-${t.id}`}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(t)} className="h-7 text-xs" data-testid={`toggle-active-${t.id}`}>
                        {t.is_active ? <Eye size={12} className="mr-1" /> : <EyeOff size={12} className="mr-1" />}
                        {t.is_active ? 'Visible' : 'Hidden'}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(t)} className="h-7 w-7 text-red-600 hover:bg-red-50" data-testid={`delete-testimonial-${t.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={creating || editing !== null} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              {creating ? 'Add Testimonial' : 'Edit Testimonial'}
            </DialogTitle>
            <DialogDescription>Keep quotes under ~80 words for the cleanest layout on the landing page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Quote *</Label>
              <Textarea
                value={form.quote}
                onChange={(e) => setForm(f => ({ ...f, quote: e.target.value }))}
                placeholder="What did this mom say about CMC?"
                rows={5}
                maxLength={800}
                className="rounded-xl"
                data-testid="testimonial-quote-input"
              />
              <p className="text-[11px] text-text-light mt-1">{form.quote.length}/800</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Stephanie R."
                  className="rounded-xl"
                  data-testid="testimonial-name-input"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Sacramento"
                  className="rounded-xl"
                  data-testid="testimonial-city-input"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-mid pt-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                data-testid="testimonial-active-checkbox"
              />
              Visible on landing page
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !form.quote.trim()}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="save-testimonial-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin mr-1" />}
              {creating ? 'Create' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
